// =============================================================
//  ChatMayaScreen — Écran de discussion avec Desk AI (assistant IA)
//  Version statique (sans API) : réponses locales via mayaResponses.
//  UI : bulles bordées (style accueil), indicateur de frappe,
//  suggestions rapides, saisie qui remonte avec le clavier,
//  upload d'images + documents (aperçu local).
//  Synchronisé entre la version mobile et la version web.
// =============================================================

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  FlatList,
  SafeAreaView,
  StatusBar,
  Keyboard,
  Platform,
  Animated,
  Easing,
  ScrollView,
  Image,
  Modal,
  Pressable,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import { useTheme } from '../../screens/ThemeContext';
import MayaIcon from './MayaIcon';
import {
  MAYA_NAME,
  WELCOME_MESSAGE,
  QUICK_REPLIES,
} from './mayaResponses';
import { askMaya } from './mayaApi';
import { parseMayaText, fetchMayaDoc } from './mayaLinks';
import { loadChatHistory, saveChatMessage } from './mayaChatStore';
import { useAuth } from '../../private/AuthContext';

let messageId = 0;
const nextId = () => `msg-${Date.now()}-${messageId++}`;

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

// ---------- Indicateur de frappe (trois points animés) ----------
function TypingIndicator({ theme }) {
  const dots = [
    useRef(new Animated.Value(0)).current,
    useRef(new Animated.Value(0)).current,
    useRef(new Animated.Value(0)).current,
  ];

  useEffect(() => {
    const animations = dots.map((dot, i) =>
      Animated.loop(
        Animated.sequence([
          Animated.delay(i * 180),
          Animated.timing(dot, {
            toValue: 1,
            duration: 400,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(dot, {
            toValue: 0,
            duration: 400,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
        ])
      )
    );
    animations.forEach((a) => a.start());
    return () => animations.forEach((a) => a.stop());
  }, []);

  return (
    <View style={[styles.bubble, styles.botBubble, { backgroundColor: theme.card, borderColor: theme.border }]}>
      <View style={styles.typingRow}>
        {dots.map((dot, i) => (
          <Animated.View
            key={i}
            style={[
              styles.typingDot,
              {
                backgroundColor: theme.primary,
                opacity: dot.interpolate({ inputRange: [0, 1], outputRange: [0.3, 1] }),
                transform: [
                  { translateY: dot.interpolate({ inputRange: [0, 1], outputRange: [0, -4] }) },
                ],
              },
            ]}
          />
        ))}
      </View>
    </View>
  );
}

// ---------- Pièce jointe (image ou document) ----------
function Attachment({ attachment, isUser, theme }) {
  if (attachment.kind === 'image') {
    return (
      <Image
        source={{ uri: attachment.uri }}
        style={styles.attachmentImage}
        resizeMode="cover"
      />
    );
  }
  // Document
  const tint = isUser ? 'rgba(255,255,255,0.9)' : theme.primary;
  const labelColor = isUser ? '#fff' : theme.text;
  const subColor = isUser ? 'rgba(255,255,255,0.75)' : theme.textSecondary;
  return (
    <View
      style={[
        styles.docCard,
        {
          backgroundColor: isUser ? 'rgba(255,255,255,0.15)' : theme.background,
          borderColor: isUser ? 'rgba(255,255,255,0.25)' : theme.border,
        },
      ]}
    >
      <MaterialCommunityIcons name="file-document-outline" size={28} color={tint} />
      <View style={styles.docInfo}>
        <Text style={[styles.docName, { color: labelColor }]} numberOfLines={1}>
          {attachment.name}
        </Text>
        {!!attachment.size && (
          <Text style={[styles.docSize, { color: subColor }]}>{formatSize(attachment.size)}</Text>
        )}
      </View>
    </View>
  );
}

// ---------- Texte bot avec liens [DOC:...] cliquables + markdown inline ----------
const styleForKind = (kind) => {
  switch (kind) {
    case 'bold':
      return { fontWeight: '700' };
    case 'italic':
      return { fontStyle: 'italic' };
    case 'underline':
      return { textDecorationLine: 'underline' };
    default:
      return null;
  }
};

function BotText({ text, theme, onDocPress }) {
  const segments = parseMayaText(text);
  return (
    <Text style={[styles.botText, { color: theme.text }]}>
      {segments.map((seg, i) => {
        if (seg.type === 'link') {
          return (
            <Text
              key={`s-${i}`}
              style={[styles.docLink, { color: theme.primary }]}
              onPress={() => onDocPress?.(seg)}
              suppressHighlighting={false}
            >
              {seg.label}
            </Text>
          );
        }
        if (seg.type === 'style') {
          return (
            <Text key={`s-${i}`} style={styleForKind(seg.styleKind)}>
              {seg.text}
            </Text>
          );
        }
        return <Text key={`s-${i}`}>{seg.text}</Text>;
      })}
    </Text>
  );
}

// ---------- Bulle de message ----------
function MessageBubble({ item, theme, onDocPress }) {
  const isUser = item.sender === 'user';
  const anim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(anim, {
      toValue: 1,
      duration: 280,
      easing: Easing.out(Easing.ease),
      useNativeDriver: true,
    }).start();
  }, []);

  const translateY = anim.interpolate({ inputRange: [0, 1], outputRange: [10, 0] });

  return (
    <Animated.View
      style={[
        styles.messageRow,
        isUser ? styles.userRow : styles.botRow,
        { opacity: anim, transform: [{ translateY }] },
      ]}
    >
      {!isUser && (
        <View style={styles.avatar}>
          <MayaIcon size={32} animated={false} />
        </View>
      )}

      {isUser ? (
        <LinearGradient
          colors={[theme.primary, theme.primaryDark]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={[styles.bubble, styles.userBubble]}
        >
          {item.attachment && <Attachment attachment={item.attachment} isUser theme={theme} />}
          {!!item.text && <Text style={styles.userText}>{item.text}</Text>}
        </LinearGradient>
      ) : (
        <View style={[styles.bubble, styles.botBubble, { backgroundColor: theme.card, borderColor: theme.border }]}>
          {item.attachment && <Attachment attachment={item.attachment} isUser={false} theme={theme} />}
          {!!item.text && <BotText text={item.text} theme={theme} onDocPress={onDocPress} />}
        </View>
      )}
    </Animated.View>
  );
}

// ---------- Écran principal ----------
export default function ChatMayaScreen({ navigation }) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const uid = user?.uid || null;
  const [messages, setMessages] = useState([
    { id: nextId(), sender: 'bot', text: WELCOME_MESSAGE },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [attachMenu, setAttachMenu] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const listRef = useRef(null);

  // Charge l'historique au montage (une fois)
  useEffect(() => {
    if (!uid || historyLoaded) return;
    let cancelled = false;
    (async () => {
      const past = await loadChatHistory(uid);
      if (cancelled) return;
      if (past.length > 0) {
        setMessages(past);
        setShowSuggestions(false);
      }
      setHistoryLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, historyLoaded]);

  const scrollToEnd = useCallback(() => {
    requestAnimationFrame(() => listRef.current?.scrollToEnd({ animated: true }));
  }, []);

  useEffect(() => {
    scrollToEnd();
  }, [messages, isTyping, scrollToEnd]);

  // Affiche un message bot (avec petit délai mini pour qu'on voie l'indicateur de frappe)
  const showBotReply = useCallback((answer) => {
    setIsTyping(false);
    setMessages((prev) => [...prev, { id: nextId(), sender: 'bot', text: answer }]);
  }, []);

  // Construit l'historique au format Claude depuis l'état messages + le nouveau texte utilisateur.
  // On exclut les messages avec pièces jointes (pas encore envoyées à Claude).
  const buildHistory = (currentMessages, userText) => {
    const history = currentMessages
      .filter((m) => !m.attachment && typeof m.text === 'string' && m.text.trim().length > 0)
      // Le tout premier message est le welcome bot — on le garde comme contexte initial
      .map((m) => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.text,
      }));
    history.push({ role: 'user', content: userText });
    return history;
  };

  const sendMessage = useCallback(
    async (rawText) => {
      const text = (rawText ?? input).trim();
      if (!text || quotaExceeded) return;
      setShowSuggestions(false);
      const userMsg = { id: nextId(), sender: 'user', text };
      setMessages((prev) => {
        const history = buildHistory(prev, text);
        setIsTyping(true);
        askMaya(history)
          .then((res) => {
            showBotReply(res.reply);
            if (typeof res.remaining === 'number') {
              setRemaining(res.remaining);
            }
            if (res.source === 'quota' || res.source === 'unauth') {
              setQuotaExceeded(true);
            }
          })
          .catch(() =>
            showBotReply(
              "Désolée, je n'arrive pas à répondre pour le moment. Réessaie ou contacte le support via WhatsApp."
            )
          );
        return [...prev, userMsg];
      });
      setInput('');
    },
    [input, showBotReply, quotaExceeded]
  );

  const sendAttachment = useCallback(
    (attachment) => {
      if (quotaExceeded) return;
      setShowSuggestions(false);
      const userMsg = { id: nextId(), sender: 'user', attachment };
      setMessages((prev) => [...prev, userMsg]);
      // Persiste la pièce jointe côté app (la Function ne la voit pas)
      if (uid) saveChatMessage(uid, userMsg);
      const answer =
        attachment.kind === 'image'
          ? "J'ai bien reçu ton image 🖼️ ! Pour l'instant je ne l'analyse pas — décris-moi en quelques mots ce que tu veux savoir dessus et je t'aide."
          : `J'ai bien reçu ton document « ${attachment.name} » 📄. Je ne l'ouvre pas pour le moment — pose-moi ta question dessus et je t'oriente !`;
      setIsTyping(true);
      setTimeout(() => {
        showBotReply(answer);
        if (uid) saveChatMessage(uid, { id: nextId(), sender: 'bot', text: answer });
      }, 400);
    },
    [showBotReply, uid, quotaExceeded]
  );

  // --- Pickers ---
  const pickImageFromGallery = useCallback(async () => {
    setAttachMenu(false);
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) return;
    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.7,
    });
    if (!res.canceled && res.assets?.length) {
      sendAttachment({ kind: 'image', uri: res.assets[0].uri });
    }
  }, [sendAttachment]);

  const takePhoto = useCallback(async () => {
    setAttachMenu(false);
    const perm = await ImagePicker.requestCameraPermissionsAsync();
    if (!perm.granted) return;
    const res = await ImagePicker.launchCameraAsync({ quality: 0.7 });
    if (!res.canceled && res.assets?.length) {
      sendAttachment({ kind: 'image', uri: res.assets[0].uri });
    }
  }, [sendAttachment]);

  const pickDocument = useCallback(async () => {
    setAttachMenu(false);
    const res = await DocumentPicker.getDocumentAsync({ type: '*/*', copyToCacheDirectory: true });
    if (!res.canceled && res.assets?.length) {
      const f = res.assets[0];
      sendAttachment({ kind: 'file', uri: f.uri, name: f.name, size: f.size });
    }
  }, [sendAttachment]);

  // Clic sur un lien de document : on charge le doc complet et on navigue.
  const handleDocPress = useCallback(
    async (link) => {
      const fullDoc = await fetchMayaDoc(link.docType, link.id);
      if (fullDoc) {
        navigation.navigate('DocumentDetailsScreenUser', { document: fullDoc });
      } else if (__DEV__) {
        console.warn('[DeskAI] document introuvable', link);
      }
    },
    [navigation]
  );

  const renderItem = useCallback(
    ({ item }) => <MessageBubble item={item} theme={theme} onDocPress={handleDocPress} />,
    [theme, handleDocPress]
  );

  // Gestion manuelle du clavier : on anime un paddingBottom synchronisé
  // sur la hauteur réelle du clavier et sur sa durée native d'animation.
  // Évite l'espace résiduel (blanc/bleu) que KeyboardAvoidingView laisse.
  const keyboardPad = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const showEvt = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvt = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';
    const onShow = (e) => {
      Animated.timing(keyboardPad, {
        toValue: e?.endCoordinates?.height ?? 280,
        duration: e?.duration ?? 250,
        easing: Easing.out(Easing.ease),
        useNativeDriver: false,
      }).start();
    };
    const onHide = (e) => {
      Animated.timing(keyboardPad, {
        toValue: 0,
        duration: e?.duration ?? 200,
        easing: Easing.out(Easing.ease),
        useNativeDriver: false,
      }).start();
    };
    const subShow = Keyboard.addListener(showEvt, onShow);
    const subHide = Keyboard.addListener(hideEvt, onHide);
    return () => {
      subShow.remove();
      subHide.remove();
    };
  }, [keyboardPad]);

  return (
    <View style={[styles.safeArea, { backgroundColor: theme.background }]}>
      <StatusBar barStyle="light-content" backgroundColor={theme.primary} />

      {/* SafeArea bleue UNIQUEMENT pour l'en-tête */}
      <SafeAreaView style={{ backgroundColor: theme.primary }}>
        <LinearGradient
          colors={[theme.primary, theme.primaryDark]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.header}
        >
          <TouchableOpacity
            style={styles.backBtn}
            onPress={() => navigation.goBack()}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <MaterialCommunityIcons name="arrow-left" size={26} color="#fff" />
          </TouchableOpacity>

          <View style={styles.headerInfo}>
            <MayaIcon size={42} animated />
            <View style={styles.headerTexts}>
              <Text style={styles.headerTitle}>{MAYA_NAME}</Text>
              <View style={styles.statusRow}>
                <View style={styles.onlineDot} />
                <Text style={styles.headerSubtitle}>
                  {quotaExceeded
                    ? 'Limite quotidienne atteinte'
                    : typeof remaining === 'number'
                    ? `Assistante IA · ${remaining} message${remaining > 1 ? 's' : ''} restant${remaining > 1 ? 's' : ''}`
                    : 'Assistante IA · en ligne'}
                </Text>
              </View>
            </View>
          </View>
        </LinearGradient>
      </SafeAreaView>

      <Animated.View
        style={[
          styles.flex,
          { backgroundColor: theme.background, paddingBottom: keyboardPad },
        ]}
      >
        <FlatList
          ref={listRef}
          data={messages}
          renderItem={renderItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          onContentSizeChange={scrollToEnd}
          ListFooterComponent={
            <>
              {isTyping && (
                <View style={[styles.messageRow, styles.botRow]}>
                  <View style={styles.avatar}>
                    <MayaIcon size={32} animated={false} />
                  </View>
                  <TypingIndicator theme={theme} />
                </View>
              )}

              {/* Suggestions rapides */}
              {showSuggestions && (
                <View style={styles.suggestionsWrap}>
                  <Text style={[styles.suggestionsLabel, { color: theme.textSecondary }]}>
                    Questions fréquentes :
                  </Text>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={styles.suggestionsRow}
                  >
                    {QUICK_REPLIES.map((q) => (
                      <TouchableOpacity
                        key={q}
                        style={[
                          styles.chip,
                          { backgroundColor: theme.card, borderColor: theme.primary + '40' },
                        ]}
                        activeOpacity={0.8}
                        onPress={() => sendMessage(q)}
                      >
                        <MaterialCommunityIcons
                          name="message-question-outline"
                          size={16}
                          color={theme.primary}
                        />
                        <Text style={[styles.chipText, { color: theme.primary }]}>{q}</Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                </View>
              )}
            </>
          }
        />

        {/* Barre de saisie — paddingBottom dynamique pour ne pas être masquée
            par la barre de navigation système Android (mode edge-to-edge) */}
        <View
          style={[
            styles.inputBar,
            {
              backgroundColor: theme.surface,
              borderTopColor: theme.border,
              paddingBottom: Math.max(insets.bottom, Platform.OS === 'ios' ? 24 : 12),
            },
          ]}
        >
          <TouchableOpacity
            style={[styles.attachBtn, { borderColor: theme.border, opacity: quotaExceeded ? 0.4 : 1 }]}
            activeOpacity={0.7}
            onPress={() => !quotaExceeded && setAttachMenu(true)}
            disabled={quotaExceeded}
          >
            <MaterialCommunityIcons name="paperclip" size={22} color={theme.primary} />
          </TouchableOpacity>

          <TextInput
            style={[
              styles.input,
              { backgroundColor: theme.background, color: theme.text, borderColor: theme.border, opacity: quotaExceeded ? 0.5 : 1 },
            ]}
            placeholder={
              quotaExceeded
                ? 'Limite atteinte — reviens demain'
                : 'Écris ton message...'
            }
            placeholderTextColor={theme.textSecondary}
            value={input}
            onChangeText={setInput}
            multiline
            maxLength={500}
            onSubmitEditing={() => sendMessage()}
            returnKeyType="send"
            blurOnSubmit={false}
            editable={!quotaExceeded}
          />
          <TouchableOpacity
            activeOpacity={0.85}
            onPress={() => sendMessage()}
            disabled={!input.trim() || quotaExceeded}
          >
            <LinearGradient
              colors={
                input.trim() && !quotaExceeded
                  ? [theme.primary, theme.primaryDark]
                  : [theme.border, theme.border]
              }
              style={styles.sendBtn}
            >
              <MaterialCommunityIcons
                name="send"
                size={22}
                color={input.trim() && !quotaExceeded ? '#fff' : theme.textSecondary}
              />
            </LinearGradient>
          </TouchableOpacity>
        </View>
      </Animated.View>

      {/* Menu d'ajout de pièce jointe */}
      <Modal
        visible={attachMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setAttachMenu(false)}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setAttachMenu(false)}>
          <Pressable
            style={[styles.attachSheet, { backgroundColor: theme.surface, borderColor: theme.border }]}
          >
            <View style={styles.sheetHandle} />
            <Text style={[styles.sheetTitle, { color: theme.text }]}>Ajouter une pièce jointe</Text>

            <TouchableOpacity style={[styles.sheetOption, { borderColor: theme.border }]} onPress={pickImageFromGallery}>
              <View style={[styles.sheetIconWrap, { backgroundColor: theme.primary + '15' }]}>
                <MaterialCommunityIcons name="image-multiple-outline" size={24} color={theme.primary} />
              </View>
              <Text style={[styles.sheetOptionText, { color: theme.text }]}>Galerie photo</Text>
            </TouchableOpacity>

            <TouchableOpacity style={[styles.sheetOption, { borderColor: theme.border }]} onPress={takePhoto}>
              <View style={[styles.sheetIconWrap, { backgroundColor: theme.primary + '15' }]}>
                <MaterialCommunityIcons name="camera-outline" size={24} color={theme.primary} />
              </View>
              <Text style={[styles.sheetOptionText, { color: theme.text }]}>Prendre une photo</Text>
            </TouchableOpacity>

            <TouchableOpacity style={[styles.sheetOption, { borderColor: theme.border }]} onPress={pickDocument}>
              <View style={[styles.sheetIconWrap, { backgroundColor: theme.primary + '15' }]}>
                <MaterialCommunityIcons name="file-document-outline" size={24} color={theme.primary} />
              </View>
              <Text style={[styles.sheetOptionText, { color: theme.text }]}>Document / PDF</Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.sheetCancel} onPress={() => setAttachMenu(false)}>
              <Text style={[styles.sheetCancelText, { color: theme.textSecondary }]}>Annuler</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1 },
  flex: { flex: 1 },

  // En-tête
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: Platform.OS === 'ios' ? 8 : 44,
    paddingBottom: 14,
    paddingHorizontal: 14,
    borderBottomLeftRadius: 22,
    borderBottomRightRadius: 22,
  },
  backBtn: { padding: 6, marginRight: 6 },
  headerInfo: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  headerTexts: { marginLeft: 12 },
  headerTitle: { color: '#fff', fontSize: 18, fontWeight: 'bold' },
  statusRow: { flexDirection: 'row', alignItems: 'center', marginTop: 2 },
  onlineDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: '#4CAF50', marginRight: 6 },
  headerSubtitle: { color: 'rgba(255,255,255,0.85)', fontSize: 12 },

  // Liste
  listContent: { paddingHorizontal: 14, paddingTop: 16, paddingBottom: 12 },
  messageRow: { flexDirection: 'row', alignItems: 'flex-end', marginBottom: 12, maxWidth: '100%' },
  userRow: { justifyContent: 'flex-end' },
  botRow: { justifyContent: 'flex-start' },
  avatar: { marginRight: 8, marginBottom: 2 },

  // Bulles (bordures, pas d'ombre)
  bubble: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 18,
    maxWidth: '78%',
    borderWidth: 1,
  },
  userBubble: { borderBottomRightRadius: 6, borderColor: 'transparent' },
  botBubble: { borderBottomLeftRadius: 6 },
  userText: { color: '#fff', fontSize: 15, lineHeight: 21 },
  docLink: { fontWeight: '700', textDecorationLine: 'underline' },
  botText: { fontSize: 15, lineHeight: 21 },

  // Pièces jointes
  attachmentImage: {
    width: 180,
    height: 180,
    borderRadius: 12,
    marginBottom: 4,
  },
  docCard: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 12,
    padding: 10,
    minWidth: 180,
    gap: 10,
  },
  docInfo: { flex: 1 },
  docName: { fontSize: 14, fontWeight: '600' },
  docSize: { fontSize: 12, marginTop: 2 },

  // Indicateur de frappe
  typingRow: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  typingDot: { width: 8, height: 8, borderRadius: 4 },

  // Suggestions
  suggestionsWrap: { marginTop: 4, marginBottom: 8 },
  suggestionsLabel: { fontSize: 12, fontWeight: '600', marginLeft: 4, marginBottom: 8 },
  suggestionsRow: { gap: 8, paddingRight: 8 },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 9,
    borderRadius: 18,
    borderWidth: 1,
  },
  chipText: { fontSize: 13, fontWeight: '600' },

  // Barre de saisie
  inputBar: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: 12,
    paddingTop: 10,
    // paddingBottom est défini dynamiquement avec safe area insets
    borderTopWidth: 1,
    gap: 8,
  },
  attachBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  input: {
    flex: 1,
    borderRadius: 22,
    borderWidth: 1,
    paddingHorizontal: 16,
    paddingTop: 11,
    paddingBottom: 11,
    fontSize: 15,
    maxHeight: 120,
  },
  sendBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Menu pièce jointe (bottom sheet)
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
  },
  attachSheet: {
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    borderWidth: 1,
    paddingHorizontal: 20,
    paddingTop: 10,
    paddingBottom: 30,
  },
  sheetHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(128,128,128,0.4)',
    alignSelf: 'center',
    marginBottom: 14,
  },
  sheetTitle: { fontSize: 16, fontWeight: 'bold', marginBottom: 14 },
  sheetOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderRadius: 14,
    borderWidth: 1,
    marginBottom: 10,
    gap: 14,
  },
  sheetIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sheetOptionText: { fontSize: 15, fontWeight: '600' },
  sheetCancel: { alignItems: 'center', paddingVertical: 12, marginTop: 4 },
  sheetCancelText: { fontSize: 15, fontWeight: '600' },
});
